import { CalendarAvailabilityModifiers } from "@/components/molecules/Calendar/modifiers/calendar-availability/calendar-availability-utils";
import { BookingScheduleTime } from "@/lib/types/schedule-types";
import { fetchDayAvailability, fetchMonthAvailability } from "~/api/calendar";
import type { ListingData } from "~/context/ListingPageDataContext";
import { ITimeAvailability } from "~/types/api-types";

import {
  getFallbackTimeOptions,
  sortMonthAvailability,
  mapTimeAvailabilityToTimeOptions,
} from "../listing-availability.utils";

type UseListingAvailabilityLoadersProps = {
  enforceMinimumHours: boolean;
  /**
   * The listing whose availability is loaded. Passed in, not read from context, so the
   * AI chat's check can use it from outside `ListingPageDataProvider`.
   */
  listingData: ListingData;
  /** Called with the calendar sync bucket after a month loads successfully. */
  onMonthAvailabilityLoaded?: (bucket: string) => void;
};

/**
 * Loaders for `ScheduleInputField`: booked days for the calendar and
 * available start/end times for the time selects.
 *
 * A failed request falls back to no booked days or the full valid time list,
 * so the picker keeps working instead of waiting on a rejected promise.
 */
const useListingAvailabilityLoaders = ({
  enforceMinimumHours,
  listingData,
  onMonthAvailabilityLoaded,
}: UseListingAvailabilityLoadersProps) => {
  const { listingId, listingParentSpaceId } = listingData;

  const getCalendarModifiers = async (
    date: Date,
  ): Promise<{ modifiers: CalendarAvailabilityModifiers }> => {
    if (!date) {
      return { modifiers: {} };
    }

    const res = await fetchMonthAvailability({
      isOwner: false,
      spaceId: listingParentSpaceId,
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    }).catch(() => null);
    if (!res) {
      return { modifiers: {} };
    }

    const modifiers = sortMonthAvailability(res.month_availability || {});
    onMonthAvailabilityLoaded?.(res.bucket);
    return { modifiers };
  };

  const getTimeOptions = async (
    date: Date,
    startTimeIndex?: number,
  ): Promise<BookingScheduleTime[]> => {
    if (!date) {
      return [];
    }

    const selector = startTimeIndex === undefined ? "start" : "end";
    const res = await fetchDayAvailability({
      selector,
      isOwner: false,
      spaceId: listingParentSpaceId,
      month: date.getMonth() + 1,
      year: date.getFullYear(),
      day: date.getDate(),
      startTimeIndex,
      listingId,
      enforceMinimumHours,
    }).catch(() => null);
    if (!res) {
      return getFallbackTimeOptions(startTimeIndex);
    }

    const availabilitySelector = `${selector}_availability`;
    const availability = (res[availabilitySelector as keyof typeof res] ||
      []) as ITimeAvailability[];
    return mapTimeAvailabilityToTimeOptions(availability);
  };

  return { getCalendarModifiers, getTimeOptions };
};

export { useListingAvailabilityLoaders };
